import { TransactionFiltersDto } from "@common/dto/transactions/getTransactions/transaction-filters.dto";
import {
    RuleGroupType,
    TransactionRuleCondition,
} from "@common/types/transactionRule";
import { getMatchingTransactionsForRule } from "@lib/transactionRules";
import { conditionFormRowToModel } from "../../../settings/Rules/TransactionRuleForm/formToModel.helpers";
import { TransactionRuleConditionFormSchema } from "../../../settings/Rules/TransactionRuleForm/constants";
import { ActionRowDefinition } from "./types";

const MATCH_PAGE_SIZE = 1000;

export type ResolvedRowCondition = Omit<TransactionRuleCondition, "id">;

interface ResolveRowConditionsParams {
    row: ActionRowDefinition;
    scopeConditions: TransactionRuleConditionFormSchema[];
}

interface FetchMatchingTransactionIdsParams {
    workspaceId: string;
    conditions: ResolvedRowCondition[];
    excludedTransactionIds?: TransactionFiltersDto["excludedTransactionIds"];
}

export function resolveRowConditions({
    row,
    scopeConditions,
}: ResolveRowConditionsParams): ResolvedRowCondition[] {
    if (row.conditions.length === 0) {
        return [];
    }
    return [...scopeConditions, ...row.conditions]
        .map(conditionFormRowToModel)
        .filter(
            (condition): condition is ResolvedRowCondition =>
                condition !== undefined,
        );
}

async function fetchMatchingTransactionIds({
    workspaceId,
    conditions,
    excludedTransactionIds,
}: FetchMatchingTransactionIdsParams): Promise<Set<number>> {
    const excludedIds = new Set<number>(excludedTransactionIds ?? []);
    const ids = new Set<number>();
    let page = 0;
    let total: number;

    do {
        const result = await getMatchingTransactionsForRule(workspaceId, {
            groupType: RuleGroupType.TRANSACTION,
            conditions,
            page,
            limit: MATCH_PAGE_SIZE,
        });

        total = result.total;
        for (const tx of result.transactions) {
            if (!excludedIds.has(tx.id)) {
                ids.add(tx.id);
            }
        }
        if (result.transactions.length === 0) {
            break;
        }
        page += 1;
    } while (page * MATCH_PAGE_SIZE < total);

    return ids;
}

export async function fetchMatchingTransactionIdsForConditions(
    params: FetchMatchingTransactionIdsParams,
): Promise<Set<number>> {
    return await fetchMatchingTransactionIds(params);
}
